import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { isAdminRole } from "@shared/roleAccessConfig";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Clock, Check, Loader2 } from "lucide-react";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";

interface CapacityResponse {
  userId: number;
  weeklyCapacityHours: number;
}

const CAPACITY_PRESETS = [
  { value: 40, label: "Full-time", description: "Standard 40-hour work week" },
  { value: 32, label: "4-day week", description: "32 hours over 4 days" },
  { value: 20, label: "Part-time", description: "Half-time schedule" },
];

export default function WorkCapacitySection() {
  const { user } = useAuth();
  const [capacity, setCapacity] = useState<number>(40);
  const [hasChanges, setHasChanges] = useState(false);
  const queryClient = useQueryClient();
  const { toast } = useToast();
  
  // Only admins and managers can edit capacity
  const canEdit = isAdminRole(user?.role) || user?.role === "manager";

  const { data: capacityData, isLoading } = useQuery<CapacityResponse>({
    queryKey: ["/api/users", user?.id, "capacity"],
    queryFn: async () => {
      const response = await fetch(`/api/users/${user?.id}/capacity`, {
        credentials: "include",
      });
      if (!response.ok) throw new Error("Failed to fetch capacity");
      return response.json();
    },
    enabled: !!user?.id,
  });

  useEffect(() => {
    if (capacityData) {
      setCapacity(capacityData.weeklyCapacityHours);
    }
  }, [capacityData]);

  const updateCapacityMutation = useMutation({
    mutationFn: async (newCapacity: number) => {
      return apiRequest(`/api/users/${user?.id}/capacity`, {
        method: "PUT",
        body: JSON.stringify({ weeklyCapacityHours: newCapacity }),
        headers: { "Content-Type": "application/json" },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/users", user?.id, "capacity"] });
      setHasChanges(false);
      toast({
        title: "Success",
        description: "Your work capacity has been updated",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to update capacity. Please try again.",
        variant: "destructive",
      });
    },
  });

  const handleCapacityChange = (value: number) => {
    const clampedValue = Math.min(60, Math.max(1, value));
    setCapacity(clampedValue);
    setHasChanges(clampedValue !== capacityData?.weeklyCapacityHours);
  };

  const handlePresetClick = (presetValue: number) => {
    setCapacity(presetValue);
    setHasChanges(presetValue !== capacityData?.weeklyCapacityHours);
  };

  const handleSave = () => {
    updateCapacityMutation.mutate(capacity);
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Work Capacity
          </CardTitle>
        </CardHeader>
        <CardContent className="flex justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="h-full">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Clock className="h-5 w-5" />
          Work Capacity
        </CardTitle>
        <CardDescription>
          {canEdit 
            ? "Set your weekly work capacity for accurate resource allocation calculations"
            : "Your weekly work capacity (contact your manager to update)"}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <label className="text-sm font-medium mb-2 block">Weekly Hours</label>
              <div className="flex items-center gap-2">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span tabIndex={0}>
                        <Input
                          type="number"
                          min={1}
                          max={60}
                          value={capacity}
                          onChange={(e) => handleCapacityChange(parseInt(e.target.value) || 1)}
                          className="w-24"
                          disabled={!canEdit}
                        />
                      </span>
                    </TooltipTrigger>
                    {!canEdit && (
                      <TooltipContent>
                        <p>Only admins and managers can edit work capacity. Contact your manager to update.</p>
                      </TooltipContent>
                    )}
                  </Tooltip>
                </TooltipProvider>
                <span className="text-muted-foreground">hours per week</span>
              </div>
            </div>
          </div>

          {canEdit && (
            <div>
              <label className="text-sm font-medium mb-2 block">Quick Presets</label>
              <div className="grid grid-cols-3 gap-3">
                {CAPACITY_PRESETS.map((preset) => (
                  <Button
                    key={preset.value}
                    variant={capacity === preset.value ? "default" : "outline"}
                    className="h-auto py-3 flex flex-col items-center gap-1"
                    onClick={() => handlePresetClick(preset.value)}
                  >
                    <span className="font-semibold">{preset.value}h</span>
                    <span className="text-xs opacity-80">{preset.label}</span>
                  </Button>
                ))}
              </div>
            </div>
          )}

          <div className="bg-muted/50 p-4 rounded-lg">
            <h4 className="font-medium mb-2">How this is used</h4>
            <p className="text-sm text-muted-foreground">
              Your weekly capacity helps calculate allocation percentages. For example, if you're allocated 20 hours on a project with a 40-hour capacity, that's 50% allocation.
            </p>
          </div>
        </div>

        {hasChanges && (
          <div className="flex justify-end pt-4 border-t">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span tabIndex={0}>
                    <Button
                      onClick={handleSave}
                      disabled={updateCapacityMutation.isPending}
                      className="gap-2"
                    >
                      {updateCapacityMutation.isPending ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Check className="h-4 w-4" />
                      )}
                      Save Changes
                    </Button>
                  </span>
                </TooltipTrigger>
                {updateCapacityMutation.isPending && (
                  <TooltipContent>
                    <p>Please wait, saving your capacity changes...</p>
                  </TooltipContent>
                )}
              </Tooltip>
            </TooltipProvider>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
